import { APITypesV1 } from "@cur8/api-client";
import { ImperialConversion } from "./measurementConfig";

// Conversion definitions (display-only — API always receives SI values)

export type ConversionDef = {
  // Labels for each input field shown in imperial mode
  fields: ReadonlyArray<{ name: string; unit: string }>;
  // SI value → display values (one per field)
  toDisplay: (si: number) => number[];
  // Display values → SI value
  toSI: (display: number[]) => number;
  // Short label shown on the toggle button
  label: string;
};

export const CONVERSION_DEFS: Record<ImperialConversion, ConversionDef> = {
  lbs: {
    fields: [{ name: "value", unit: "lbs" }],
    label: "lbs",
    toDisplay: (kg) => [round1(kg * 2.20462)],
    toSI: ([lbs]) => round1(lbs / 2.20462),
  },
  st_lbs: {
    fields: [
      { name: "st", unit: "st" },
      { name: "lbs", unit: "lbs" },
    ],
    label: "st / lbs",
    toDisplay: (kg) => {
      const totalLbs = kg * 2.20462;
      return [Math.floor(totalLbs / 14), round1(totalLbs % 14)];
    },
    toSI: ([st, lbs]) => round1((st * 14 + lbs) / 2.20462),
  },
  ft_in: {
    fields: [
      { name: "ft", unit: "ft" },
      { name: "in", unit: "in" },
    ],
    label: "ft / in",
    toDisplay: (cm) => {
      const totalIn = cm / 2.54;
      return [Math.floor(totalIn / 12), round1(totalIn % 12)];
    },
    toSI: ([ft, inches]) => round1((ft * 12 + inches) * 2.54),
  },
};

export function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

export function isOutOfRange(
  value: number,
  range: APITypesV1.RiskRange
): boolean {
  for (const interval of range.riskIntervals) {
    if (
      interval.riskLevel === APITypesV1.RiskType.Risk ||
      interval.riskLevel === APITypesV1.RiskType.HighRisk ||
      interval.riskLevel === APITypesV1.RiskType.ModerateRisk ||
      interval.riskLevel === APITypesV1.RiskType.ImmediateRisk
    ) {
      const start = interval.start.value;
      const end = interval.end.value;
      const startInclusive = interval.start.inclusive ?? true;
      const endInclusive = interval.end.inclusive ?? true;

      const aboveStart =
        start == null || (startInclusive ? value >= start : value > start);
      const belowEnd =
        end == null || (endInclusive ? value <= end : value < end);

      if (aboveStart && belowEnd) {
        return true;
      }
    }
  }
  return false;
}
